<template>
  <form
    ref="formElement"
    :class="['UIForm', { 'UIForm--has-actions': $slots['actions'] }]"
  >
    <slot />

    <UIFormActions v-if="$slots['actions']">
      <slot name="actions" />
    </UIFormActions>

    <UIFormIsDirtyModal
      v-bind="isDirtyModalOpen"
      @discard="discardChanges()"
      @close="isDirtyModalOpen.close()"
    />
  </form>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, provide, reactive } from 'vue'

import { useFormContext } from 'vee-validate'
import { onBeforeRouteLeave } from 'vue-router'

import { useModal } from '@/composables/useModal'

import UIFormActions from './UIFormActions.vue'
import UIFormIsDirtyModal from './UIFormIsDirtyModal.vue'

const props = defineProps<{
  /**
   * Whether to skip the form dirty modal
   * @default false
   */
  skipFormDirtyModal?: boolean
  /**
   * Prompt on leave even when the form is pristine (e.g. an unfinalized draft
   * that must be discarded). The `discard` event lets the parent clean up.
   * @default false
   */
  forcePrompt?: boolean
}>()

const emit = defineEmits<{
  discard: []
}>()

const formElement = ref<HTMLFormElement>()
const isDirtyModalOpen = useModal<() => void>()
const { meta } = useFormContext()

// Form dimensions for UIFormActions positioning
const formDimensions = reactive({
  width: 0,
  rightSpacing: 0,
})

const measureFormDimensions = () => {
  if (!formElement.value) return

  const formRect = formElement.value.getBoundingClientRect()
  const viewportWidth = window.innerWidth

  formDimensions.width = formRect.width
  formDimensions.rightSpacing = viewportWidth - formRect.right
}

// Provide form dimensions to child components
provide('formDimensions', formDimensions)

onMounted(() => {
  measureFormDimensions()

  // Re-measure on window resize
  window.addEventListener('resize', measureFormDimensions)
})

// Cleanup event listener
onUnmounted(() => {
  window.removeEventListener('resize', measureFormDimensions)
})

const handleRouteChange = (_to: unknown, _from: unknown, next: () => void) => {
  if ((meta.value.dirty || props.forcePrompt) && !props.skipFormDirtyModal) {
    isDirtyModalOpen.open(next)
  } else {
    next()
  }
}

const discardChanges = () => {
  // Let the parent clean up (e.g. delete the draft) before navigating.
  emit('discard')
  isDirtyModalOpen.modalData?.()
  isDirtyModalOpen.close()
}

onBeforeRouteLeave(handleRouteChange)
</script>

<style lang="scss" scoped>
.UIForm {
  &.UIForm--has-actions {
    padding-bottom: 4rem;
  }
}
</style>
